import helpIcon from '@jetbrains/icons/help-20px.svg'
import Button from '@jetbrains/ring-ui/components/button/button'
import Dropdown from '@jetbrains/ring-ui/components/dropdown/dropdown'
import {TrayIcon} from '@jetbrains/ring-ui/components/header/header'
import Tray from '@jetbrains/ring-ui/components/header/tray'
import type {ListDataItem} from '@jetbrains/ring-ui/components/list/list'
import {Directions} from '@jetbrains/ring-ui/components/popup/popup.consts'
import PopupMenu from '@jetbrains/ring-ui/components/popup-menu/popup-menu'
import classNames from 'classnames'
import memoizeOne from 'memoize-one'
import * as React from 'react'
import {useContext} from 'react'
import {graphql, useFragment} from 'react-relay'

import Measure from '../../../containers/Measure'
import {CurrentUserKeyContext} from '../../../contexts/currentUserKey'
import {useIsPipelinesSelected} from '../../../hooks/routes'
import {useTheme} from '../../../hooks/useTheme'
import {PlaceIdList} from '../../../plugins/plugins.types'
import {getIsSakuraUI} from '../../../selectors'
import {GUEST_ID, SUPER_USER_ID} from '../../../types'
import {BS} from '../../../types/BS_types'
import {Size} from '../../common/Avatar/Avatar.container'
import {AvatarRelay} from '../../common/Avatar/Avatar.relay'
import Plugin from '../../common/Plugin/Plugin'
import {headerUserProfileLinkHint} from '../../packages/Hints/HintItems/Hints.navigation'
import {useHint} from '../../packages/Hints/Hints.hooks'
import {
  isPipelinesEnabled,
  isPipelinesEnabledInExclusiveMode,
} from '../../pages/PipelinesPages/utils/featureToggles'

import type {HeaderUserFragment$key} from './__generated__/HeaderUserFragment.graphql'
import {LINKS} from './Header.constants'
import HeaderTrayHelp from './HeaderTrayHelp/HeaderTrayHelp'
import AdministrationLink from './Links/Links.administrator'
import {TemporaryPromotionalThemeSwitcher} from './TemporaryPromotionalThemeSwitcher/TemporaryPromotionalThemeSwitcher'

import styles from './Header.css'

import {useAppSelector} from 'src/hooks/react-redux'

const PopupDirections = [Directions.BOTTOM_LEFT]

//eslint-disable-next-line new-cap
const logout = () => BS?.Logout?.(LINKS.logout.classic)

const getPopupMenuItems = memoizeOne(
  (isSuperUser: boolean, isSakuraUI: boolean): ListDataItem[] => {
    const field = isSakuraUI ? 'sakura' : 'classic'
    const favoriteBuilds = LINKS.favoriteBuilds[field]
    const investigations = LINKS.investigations[field] ?? LINKS.investigations.classic
    const popupMenu: ListDataItem[] = []

    if (!isSuperUser) {
      popupMenu.push({
        key: LINKS.profile.classic,
        label: 'Profile',
        href: LINKS.profile.classic,
      })
    }

    if (!isSuperUser && !isPipelinesEnabledInExclusiveMode) {
      popupMenu.push(
        {
          key: favoriteBuilds,
          label: 'Favorite Builds',
          href: favoriteBuilds,
        },
        {
          key: investigations,
          label: 'Investigations',
          href: investigations,
        },
      )
    }

    popupMenu.push({
      key: LINKS.logout.classic,
      label: 'Logout',
      href: '#',
      onClick: logout,
    })

    return popupMenu
  },
)

const headerUserFragment = graphql`
  fragment HeaderUserFragment on User {
    rawId
    name
    username
    ...AvatarFragment
  }
`

const HeaderUser = React.memo(() => {
  useHint(headerUserProfileLinkHint)
  const isSakuraUI = useAppSelector(getIsSakuraUI)
  const userKey: HeaderUserFragment$key | null = useContext(CurrentUserKeyContext)
  const user = useFragment(headerUserFragment, userKey)
  const userId = user?.rawId
  const userName = user?.name ?? user?.username
  const menuItems = getPopupMenuItems(SUPER_USER_ID === userId, isSakuraUI)

  if (userId === GUEST_ID) {
    return (
      <Button
        onClick={logout}
        className={classNames(styles.login, styles.guest)}
        primary
        href={LINKS.login.classic}
      >
        {'Log in...'}
      </Button>
    )
  }

  return (
    <Dropdown
      data-hint-container-id={headerUserProfileLinkHint.id}
      className={classNames(styles.text, styles.login)}
      anchor={
        <button
          title={userName ?? undefined}
          aria-label={userName ?? undefined}
          type="button"
          className={styles.avatarWrapper}
        >
          {user && (
            <AvatarRelay
              userKey={user}
              size={Size.Size32}
              className={styles.avatar}
              avatarClassName={styles.avatarInner}
              withOutline={false}
            />
          )}
        </button>
      }
    >
      <PopupMenu directions={PopupDirections} top={-12} data={menuItems} />
    </Dropdown>
  )
})
HeaderUser.displayName = 'HeaderUser'
type Props = {
  adminSpaceAvailable: boolean
}

function HeaderTray({adminSpaceAvailable}: Props) {
  const {canShowThemeSwitcher} = useTheme()
  const isPipelinesSelected = useIsPipelinesSelected()
  const isPipelines =
    isPipelinesEnabledInExclusiveMode || (isPipelinesEnabled && isPipelinesSelected)

  return (
    <Tray className={classNames(styles.tray, 'header-tray')} id={'userPanel'}>
      <div className={classNames(styles.headerModule1, styles.trayItem)} id={'header-module-1'}>
        <span className="investigationsTicker" />
      </div>
      {canShowThemeSwitcher && <TemporaryPromotionalThemeSwitcher />}
      <Plugin placeId={PlaceIdList.SAKURA_HEADER_USERNAME_BEFORE} />
      {adminSpaceAvailable && <AdministrationLink />}
      {isPipelines ? (
        <TrayIcon
          title="Help"
          icon={helpIcon}
          href="https://www.jetbrains.com/help/teamcity/pipelines/teamcity-pipelines.html"
          target="_blank"
          rel="noreferrer"
        />
      ) : (
        <HeaderTrayHelp />
      )}
      <Measure offset>
        {({measureRef}) => (
          <div className={styles.rightWrapper}>
            <div ref={measureRef}>
              <Plugin placeId={PlaceIdList.SAKURA_HEADER_RIGHT} />
            </div>
          </div>
        )}
      </Measure>
      <HeaderUser />
      <span id="header-ready" />
    </Tray>
  )
}

export default React.memo(HeaderTray)
